<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";

import { cardKey } from "@/components/StickyNote/injectKeys";
import { isDependency } from "@/model/stickyType";
import { useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useZoomStore } from "@/store/zoom";
import { optimalDisplayFontSize, optimalEditFontSize } from "@/utils/text/fontSizeOptimizer";
import { injectStrict } from "@/utils/vue";

const DEFAULT_FONT_SIZE = 300;

const props = defineProps<{
  notOnBoard?: boolean; // When rendered on a board, the card size is fixed (so we can optimize the font-size calculation)
  renderedEl?: HTMLElement | null;
  isEditing?: boolean;
}>();

onMounted(() => {
  watch(() => card.text, calculateOptimalFontSize, { immediate: true });
  watch([() => useZoomStore().factor, () => useBoardStore().board?.id], () =>
    calculateOptimalFontSize(card.text),
  );
});

const card = injectStrict(cardKey);

const fontSize = ref(DEFAULT_FONT_SIZE);
const lines = ref<string[]>([]);

const calculateOptimalFontSize = async (value: string) => {
  await nextTick();

  if (!props.renderedEl?.clientHeight) return;

  if (props.isEditing) {
    const fontData = optimalEditFontSize(
      props.renderedEl,
      value,
      useClientSettingsStore().stickyFont,
      { isDependency: isDependency(card), nonStandardCardSize: props.notOnBoard },
    );
    fontSize.value = fontData.size;
  } else {
    const fontData = optimalDisplayFontSize(
      props.renderedEl,
      value,
      useClientSettingsStore().stickyFont,
      { isDependency: isDependency(card), nonStandardCardSize: props.notOnBoard },
    );
    fontSize.value = fontData.size;
    lines.value = fontData.htmlLines;
  }
};

defineExpose({ recalculateFontSize: calculateOptimalFontSize });
</script>

<template>
  <div
    class="sticky-note-text-wrapper"
    :class="{
      centered: useClientSettingsStore().textAlignment === 'center',
      'right-align': useClientSettingsStore().textAlignment === 'right',
    }"
    :data-type="card.type.functionality"
  >
    <slot :font-size :lines />
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.sticky-note-text-wrapper {
  font-family: var(--card-font);
  overflow: hidden;
  margin: 4px;
  padding: 4px;

  &:focus-within {
    background-color: colors.$palette-black-alpha-5;
  }

  &.centered {
    text-align: center;
  }

  &.right-align {
    text-align: right;
  }
}
</style>
